import * as React from "react";
import { cn } from "@/lib/cn";

/**
 * A labelled on/off control. Appearance is `.switch` in `styles/ui.css`.
 *
 * `<button role="switch" aria-checked>` rather than `<input type="checkbox">`.
 * The checkbox is the right element for "include this in a set" - which is what
 * `MultiSelect` draws - and the wrong one for "this setting is on now", because
 * a checkbox implies its effect lands when the form is submitted. A switch acts
 * immediately, and here it changes what the rest of the form shows.
 *
 * The whole row is the target rather than the track alone. A 44px pill beside a
 * label people naturally aim at is a control that misses.
 */
export function Switch({
  id,
  checked,
  onChange,
  label,
  description,
  disabled,
  inline,
}: {
  id?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  /** The second line. For a setting whose consequence is not obvious from its name. */
  description?: string;
  disabled?: boolean;
  /**
   * No box: the label and the track, sized to their content. For a switch that
   * sits in a row of other controls - a list row, a form's action bar - where a
   * full-width bordered field would be a second box inside the row.
   */
  inline?: boolean;
}) {
  const descriptionId = React.useId();

  return (
    <button
      type="button"
      id={id}
      role="switch"
      aria-checked={checked}
      aria-describedby={description ? descriptionId : undefined}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn("switch", checked && "switch--on", inline && "switch--inline")}
    >
      <span className="switch__text">
        <span className="switch__label">{label}</span>
        {description && (
          <span id={descriptionId} className="switch__description">
            {description}
          </span>
        )}
      </span>
      <span className="switch__track" aria-hidden>
        <span className="switch__thumb" />
      </span>
    </button>
  );
}
